/**
 * Geospatial Math Utilities
 * Pure functions — no React, no side effects.
 */

const EARTH_RADIUS_FT = 20_902_464; // Earth's mean radius in feet

/**
 * Convert degrees to radians
 */
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Calculate the distance between two GPS coordinates using the Haversine formula.
 * @returns Distance in feet
 */
export const haversineDistanceFt = (
    coord1: { lat: number; lng: number },
    coord2: { lat: number; lng: number }
): number => {
    const dLat = toRad(coord2.lat - coord1.lat);
    const dLng = toRad(coord2.lng - coord1.lng);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(coord1.lat)) *
        Math.cos(toRad(coord2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_FT * c;
};

/*
 * Aquí vivía `isWithinGeofence`, sin un solo llamador y con su PROPIO radio
 * por defecto de 500 ft escrito a mano. Ese era el problema: cada sitio que
 * inventa su radio es otro número que contradice al de ajustes, y quien la
 * encontrara la habría usado creyendo que respeta la configuración del taller.
 * Quien necesite comparar contra un radio: `haversineDistanceFt` y el radio
 * que venga de los ajustes, en el sitio de la llamada.
 */

/**
 * El radio de una geocerca, en pies: el respaldo, el suelo y el techo.
 *
 * Los tres números ya existían repartidos por la app escritos a mano, y no
 * coincidían: la tarjeta de la obra respaldaba a 300 en un botón y a 500 en el
 * otro —dos caminos de la misma tarjeta dando geocercas distintas—, y el
 * difunto `isWithinGeofence` traía un tercero. Aquí hay UNO, y es el mismo que
 * acota la columna en la base (CHECK 100..5280): si el formulario deja pasar
 * algo fuera de rango, la escritura revienta con un error de Postgres que no
 * dice nada.
 *
 * Por qué esas cotas: por debajo de 100 ft nadie ficha nunca dentro —el GPS de
 * un teléfono ya yerra decenas de pies— y 5280 es una milla, techo de sobra
 * para la obra más grande.
 */
export const DEFAULT_GEOFENCE_FT = 300;
export const MIN_GEOFENCE_FT = 100;
export const MAX_GEOFENCE_FT = 5280;

/**
 * Deja un radio dentro de las cotas de la base de datos.
 *
 * Acepta `unknown` porque lo que llega es el `value` de un `<input>`: una
 * CADENA, y a veces vacía. Se distinguen dos casos a propósito:
 *
 *  - Lo que no es un número (campo vacío, null, 'abc', NaN) → el respaldo. No
 *    hay intención que respetar, y devolver 0 dejaría el taller sin geocerca.
 *  - Un número de verdad fuera de rango → la cota más cercana. Quien teclea
 *    50000 quiere «lo más ancho posible», no que se le ignore el cambio.
 *
 * Se redondea porque la columna es `int`: un 450.6 lo rechaza Postgres.
 */
export const clampGeofenceRadiusFt = (value: unknown): number => {
    const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').trim());
    if (!Number.isFinite(n)) return DEFAULT_GEOFENCE_FT;
    return Math.min(MAX_GEOFENCE_FT, Math.max(MIN_GEOFENCE_FT, Math.round(n)));
};

/**
 * Geocode an address string to lat/lng coordinates.
 *
 * Prefers the Maps JavaScript API geocoder (loaded app-wide by
 * GoogleMapsProvider), which WORKS with HTTP-referrer-restricted keys. The
 * Geocoding REST web service rejects referrer-restricted keys outright —
 * every call returned REQUEST_DENIED in production once the key was locked
 * to razaproapp.com (security audit H-Maps), silently breaking project
 * geofences from ~06-05. The REST path remains only as a fallback for
 * non-browser/SSR contexts or before the JS API finishes loading.
 *
 * @param address The street address to geocode
 * @param apiKey Google Maps API key (only used by the REST fallback)
 * @returns { lat, lng } or null if geocoding fails
 */
export const geocodeAddress = async (
    address: string,
    apiKey?: string
): Promise<{ lat: number; lng: number } | null> => {
    const query = (address || '').trim();
    if (!query) return null;

    // Preferred: Maps JS API geocoder — referrer-restriction-safe.
    if (typeof google !== 'undefined' && google.maps?.Geocoder) {
        return new Promise((resolve) => {
            new google.maps.Geocoder().geocode({ address: query }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    resolve({ lat: loc.lat(), lng: loc.lng() });
                } else {
                    console.warn(`[geomath] Geocoding ${status} for:`, query);
                    resolve(null);
                }
            });
        });
    }

    // Fallback: REST web service. REQUIRES a key without referrer restrictions;
    // with the production key this will REQUEST_DENIED — kept only as a last
    // resort for contexts where the JS API never loads.
    if (!apiKey) return null;
    try {
        const encoded = encodeURIComponent(query);
        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encoded}&key=${apiKey}`
        );
        const data = await response.json();

        if (data.status === 'OK' && data.results.length > 0) {
            const { lat, lng } = data.results[0].geometry.location;
            return { lat, lng };
        }

        console.warn('[geomath] Geocoding returned no results for:', query, data?.status);
        return null;
    } catch (err) {
        console.error('[geomath] Geocoding failed:', err);
        return null;
    }
};
